import { useCallback, useEffect, useRef } from "react";
import { nextPollDelayMs, useScoreboardPoll } from "common-fe/scoreboard/useScoreboardPoll.js";

// Card #3560 -- the public boards (venue scoreboard, phone board, Live page) run
// a CHANGE CHECK instead of pulling the whole composed payload every beat
// (docs/adr/0003, GLOSSARY "Live screens"). Same design as announcer-fe's and
// scoreadmin-fe's useShowChangeCheck (#3557, #3559), reshaped for a screen whose
// data is ONE composed payload and which must NEVER show an error.
//
// Every beat asks the show's change signal (fetchChanges, score-api #3558: a few
// counters, ONE DynamoDB read, no-store for the venue board, CDN-cached 2s for
// phones) and pulls the payload only when something says it is due:
//
//   - the signal MOVED (`total`: any change to the show, scoring or a board
//     view edit, #3560 makes mode / ride-pick / screen writes bump it; or
//     `showVersion`);
//   - the one extra read OWED after a move, and one after the baseline is first
//     adopted (a write between the payload load and the first check would
//     otherwise be missed). Only a pull that LANDED pays an owed read off, so a
//     failed pull is retried on the next beat. (the scribe #3252 pattern: the read
//     that follows a counter move can be eventually consistent, and a phone's
//     copy can be up to 2 s old at the edge, so the first read may predate the
//     write that moved the counter);
//   - the payload's own wall clock came due (`nextClockChangeMs`: a class
//     complete countdown, a mode group rotation, the next slide change nothing
//     writes, so no counter moves);
//   - the BACKSTOP: no pull for backstopMs (60 s), so a counter the signal does
//     not cover can never freeze a board for longer than that;
//   - nothing has loaded yet (the first pull failed, or the show id is not
//     known yet): pull every beat, the old cadence, so a board that starts
//     before the network does fills in the moment it can.
//
// A FAILED check changes nothing: no pull, no baseline, no error; the next beat
// asks again. A failed PULL keeps the last good payload on screen (the shared
// useScoreboardPoll never clears `data`); the board falls back to Blank + logo
// by its own rules, it never shows an error.
//
// The first answer after a payload loads ADOPTS the baseline (the page already
// loaded its data on mount) and owes one confirming read. Counters only go up:
// an older copy (a CDN edge a request behind) is neither a move nor a lower
// baseline. The show id the check asks about is the
// payload's own `data.showId`, never the route param (a short URL may carry a
// usefCompId, #1866).
//
// Cadence: the venue board beats on a fixed setInterval (1 s, the #1907 AC9
// freshness budget). The phone board and the Live page beat on a setTimeout
// chain whose wait is the server's `pollAfterMs` plus jitter (#3302), so a crowd
// that loaded together does not check in lockstep.
//
// SEAM for card 3562's staff nudges (the venue board gets nudges, public phones
// never do): `checkNow()` runs one beat right now, and `checkNowSignal` (any
// value that changes) does the same from outside.
export const VENUE_BEAT_MS = 1000;
export const PHONE_FALLBACK_BEAT_MS = 2000;
export const PHONE_JITTER_RATIO = 0.2;
export const BOARD_BACKSTOP_MS = 60 * 1000;
// A clock-driven pull waits this much PAST the due time so the composed answer
// has certainly crossed it.
export const CLOCK_PULL_MARGIN_MS = 250;

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const isHidden = () => typeof document !== "undefined" && document.visibilityState === "hidden";

export function useBoardChangeCheck(
  fetchPayload,
  fetchChanges,
  {
    phone = false,
    enabled = true,
    backstopMs = BOARD_BACKSTOP_MS,
    fallbackBeatMs = PHONE_FALLBACK_BEAT_MS,
    jitterRatio = PHONE_JITTER_RATIO,
    random = Math.random,
    checkNowSignal,
  } = {}
) {
  const fetchRef = useRef(fetchPayload);
  fetchRef.current = fetchPayload;
  const changesRef = useRef(fetchChanges);
  changesRef.current = fetchChanges;
  const phoneRef = useRef(phone);
  phoneRef.current = phone;
  const optsRef = useRef({ backstopMs, fallbackBeatMs, jitterRatio, random });
  optsRef.current = { backstopMs, fallbackBeatMs, jitterRatio, random };

  // What the last APPLIED payload said about itself. Refs, not state: a beat must
  // not re-render the board. Derived from `data` below (the response the poll
  // actually applied), never from a response that was fetched but dropped by the
  // poll's stale-response fence.
  const metaRef = useRef({ loaded: false, showId: null, pollAfterMs: undefined });
  // owedReads: successful pulls still owed after a move (or after adopting the
  // baseline). A move owes TWO (the scribe #3252 pattern: the read that follows a
  // counter move can be eventually consistent, and a phone's copy can be up to 2 s
  // old at the edge); only a pull that LANDED pays one off, so a failed pull keeps
  // it owed and the next beat pulls again.
  const st = useRef({ last: null, lastShowId: null, owedReads: 0, sinceLastPull: 0, clockLeft: null, inflight: false, okCount: 0, pulling: 0 });

  const wrapped = useCallback(async () => {
    const result = await fetchRef.current();
    st.current.okCount += 1; // the fetch landed (a throw above never counts)
    return result;
  }, []);

  const { data, error, lastUpdated, refresh } = useScoreboardPoll(wrapped, { intervalMs: 0, enabled });
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  const appliedRef = useRef(null);
  if (data != null && data !== appliedRef.current) {
    appliedRef.current = data;
    metaRef.current = { loaded: true, showId: data.showId ?? null, pollAfterMs: data.pollAfterMs };
    const clock = Number(data.nextClockChangeMs);
    st.current.clockLeft = Number.isFinite(clock) && clock > 0 ? clock + CLOCK_PULL_MARGIN_MS : null;
  }

  // Resolves true when the pull LANDED. refresh() swallows errors (the poll keeps
  // the last good payload), so success is read off the fetch itself.
  const pull = useCallback(async () => {
    const before = st.current.okCount;
    st.current.pulling += 1;
    try {
      await refreshRef.current();
    } finally {
      st.current.pulling -= 1;
    }
    const ok = st.current.okCount > before;
    if (ok) {
      st.current.sinceLastPull = 0;
      st.current.owedReads = Math.max(0, st.current.owedReads - 1);
    }
    return ok;
  }, []);

  // A phone's beat waits for its pull (one request at a time, the next wait starts
  // after). The venue board does NOT: a slow pull must not hold the next 1 s check
  // back (#2309: the poll's stale-response fence drops a late older answer), but
  // there are never more than two pulls in flight.
  const firePull = useCallback(async () => {
    if (phoneRef.current) await pull();
    else if (st.current.pulling < 2) pull();
  }, [pull]);

  // One beat. `elapsedMs` is how long since the previous beat (the clock and
  // backstop counters run on beats, not on the Date, so a paused tab never
  // counts the time it was away).
  const beat = useCallback(
    async (elapsedMs) => {
      const s = st.current;
      if (s.inflight) return;
      s.inflight = true;
      try {
        s.sinceLastPull += elapsedMs;
        if (s.clockLeft != null) s.clockLeft -= elapsedMs;
        const meta = metaRef.current;
        if (!meta.loaded || !meta.showId) {
          await firePull();
          return;
        }
        if (s.lastShowId !== meta.showId) {
          s.lastShowId = meta.showId;
          s.last = null;
          s.owedReads = 0;
        }
        let signal = null;
        try {
          signal = await changesRef.current({ showId: meta.showId });
        } catch {
          signal = null; // a failed check changes nothing
        }
        let due = false;
        if (signal) {
          const total = num(signal.total);
          const showVersion = num(signal.showVersion);
          const prev = s.last;
          // Counters only go up. An older copy (a CDN edge a request behind) is
          // neither a move nor a lower baseline.
          const moved = prev !== null && (total > prev.total || showVersion > prev.showVersion);
          s.last = prev === null ? { total, showVersion } : { total: Math.max(total, prev.total), showVersion: Math.max(showVersion, prev.showVersion) };
          if (prev === null) s.owedReads = Math.max(s.owedReads, 1); // a write between the payload load and this first check
          if (moved) s.owedReads = 2;
        }
        due = s.owedReads > 0;
        if (!due && s.sinceLastPull >= optsRef.current.backstopMs) due = true;
        if (!due && s.clockLeft != null && s.clockLeft <= 0) due = true;
        if (due) await firePull();
      } finally {
        s.inflight = false;
      }
    },
    [firePull]
  );
  const beatRef = useRef(beat);
  beatRef.current = beat;

  // The phone wait is the SERVER's pollAfterMs (a pinned scale-up must not be
  // undercut). A wait is armed from the last APPLIED payload, but the payload that
  // carries a new pollAfterMs commits just after the beat that pulled it armed the
  // next timer, and the very first wait is armed at mount before any payload
  // exists. So when an applied payload names a different pollAfterMs than the
  // armed wait used, re-arm the timer from it.
  const rearmRef = useRef(null);
  const armedForRef = useRef(undefined);
  useEffect(() => {
    if (data == null || !rearmRef.current) return;
    if (armedForRef.current !== data.pollAfterMs) rearmRef.current();
  }, [data]);

  useEffect(() => {
    if (!enabled) return undefined;

    if (!phone) {
      let timer = null;
      const start = () => {
        if (timer == null && !isHidden()) timer = setInterval(() => beatRef.current(VENUE_BEAT_MS), VENUE_BEAT_MS);
      };
      const stop = () => {
        if (timer != null) {
          clearInterval(timer);
          timer = null;
        }
      };
      const onVisibility = () => {
        if (isHidden()) {
          stop();
        } else {
          pull(); // back on screen: read now, the old behaviour
          start();
        }
      };
      start();
      document.addEventListener("visibilitychange", onVisibility);
      return () => {
        stop();
        document.removeEventListener("visibilitychange", onVisibility);
      };
    }

    let timer = null;
    let gen = 0;
    const loop = (myGen) => {
      const o = optsRef.current;
      armedForRef.current = metaRef.current.pollAfterMs;
      const waitMs = nextPollDelayMs(metaRef.current.pollAfterMs, o.fallbackBeatMs, {
        jitterRatio: o.jitterRatio,
        random: o.random,
      });
      timer = setTimeout(async () => {
        timer = null;
        await beatRef.current(waitMs);
        if (myGen !== gen || isHidden()) return;
        loop(myGen);
      }, waitMs);
    };
    const halt = () => {
      gen += 1;
      if (timer != null) {
        clearTimeout(timer);
        timer = null;
      }
    };
    const begin = () => {
      halt();
      if (!isHidden()) loop(gen);
    };
    rearmRef.current = begin;
    const onPhoneVisibility = () => {
      if (isHidden()) {
        halt();
      } else {
        pull();
        begin();
      }
    };
    begin();
    document.addEventListener("visibilitychange", onPhoneVisibility);
    return () => {
      rearmRef.current = null;
      halt();
      document.removeEventListener("visibilitychange", onPhoneVisibility);
    };
  }, [enabled, phone, pull]);

  // A different board (route, year, slug or code changed): forget the baseline
  // and read the new one now. The old payload stays up until it lands.
  const firstFetchRef = useRef(true);
  useEffect(() => {
    if (firstFetchRef.current) {
      firstFetchRef.current = false;
      return;
    }
    metaRef.current = { loaded: false, showId: null, pollAfterMs: undefined };
    st.current.last = null;
    st.current.lastShowId = null;
    st.current.owedReads = 0;
    pull();
  }, [fetchPayload, pull]);

  const checkNow = useCallback(() => beatRef.current(0), []);

  // An outside trigger (card 3562): any change in checkNowSignal asks right now.
  const firstSignalRef = useRef(true);
  useEffect(() => {
    if (firstSignalRef.current) {
      firstSignalRef.current = false;
      return;
    }
    checkNow();
  }, [checkNowSignal, checkNow]);

  return { data, error, lastUpdated, refresh, checkNow };
}

export default useBoardChangeCheck;
